import { Button, InputAdornment, Stack, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import ModalV2 from '/@/components/bayer/modal-v2';
import useUnitProvider from '/@/core/context/unit';
import { revalidateMyFields, useUpdateFieldMutation } from '/@/hooks/fields';
import { convertAreaFromM2, convertAreaToM2 } from '/@/hooks/geometry/area';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';

const FieldEditModal = ({ open, onClose, field }: { open: boolean; onClose: () => void; field: Field }) => {
    const units = useUnitProvider();
    const country = useSelector(getSessionCountry);
    const form = useForm({
        defaultValues: {
            name: field.name,
            area: convertAreaFromM2(field.area || 0, units).value,
            // enabledSendFrostRiskNotification: field.enabledSendFrostRiskNotification,
        },
    });

    const updateField = useUpdateFieldMutation(field.id);

    const onSubmit = async (data: { name: string; area: number }) => {
        try {
            await updateField.trigger({
                name: data.name,
                area: Number(convertAreaToM2(data.area, units).value),
                // enabledSendFrostRiskNotification: data.enabledSendFrostRiskNotification,
            });
            mpTrackEvent('Field edited', undefined);
            enqueueSnackbar(_('Lote actualizado correctamente'), {
                SnackbarProps: { 'data-testid': 'edit-field-modal-snackbar-success' },
                variant: 'success',
            });
            onClose();
            revalidateMyFields({ country: country.code!, fieldId: field.id });
        } catch (error) {
            enqueueSnackbar(_('Error al actualizar el lote'), { variant: 'error' });
        }
    };

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            title={_('Editar lote')}
            actions={
                <>
                    <Button type='button' variant='outlined' onClick={onClose} disabled={updateField.isMutating}>
                        {_('Cancelar')}
                    </Button>
                    <Button
                        type='submit'
                        variant='contained'
                        color='primary'
                        disabled={updateField.isMutating}
                        loading={updateField.isMutating}
                        onClick={form.handleSubmit(onSubmit)}
                        data-testid='edit-field-modal-button-save'
                    >
                        {_('Guardar')}
                    </Button>
                </>
            }
        >
            <FormProvider {...form}>
                <Stack spacing={3}>
                    <Controller
                        name='name'
                        control={form.control}
                        rules={{ required: _('El nombre es requerido') }}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                {...field}
                                label={_('Nombre')}
                                fullWidth
                                error={!!error}
                                helperText={error?.message}
                                data-testid='edit-field-modal-input-name'
                            />
                        )}
                    />
                    <Controller
                        name='area'
                        control={form.control}
                        rules={{
                            required: _('El área es requerida'),
                            min: { value: 0, message: _('El área debe ser mayor a 0') },
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>{units.getUnit('area', 'corn')}</InputAdornment>
                                    ),
                                }}
                                {...field}
                                label={_('Área')}
                                fullWidth
                                type='number'
                                inputProps={{ min: 0, step: 0.01 }}
                                error={!!error}
                                helperText={error?.message}
                                data-testid='edit-field-modal-input-area'
                            />
                        )}
                    />
                    {/*  
                        <Controller
                            name='enabledSendFrostRiskNotification'
                            control={form.control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Checkbox {...field} checked={field.value} />}
                                    label={_('Notificar riesgo de helada')}
                                />
                            )}
                        /> */}
                </Stack>
            </FormProvider>
        </ModalV2>
    );
};

export default FieldEditModal;
